import React, { useEffect, useState } from 'react';
import { Modal } from '../Common/Modal';
import Alert from '../Common/Alert';
import visitService from '../../services/visitService';
import { VISIT_TYPES } from '../../utils/constants';
import { visits as visitMessages, common as commonMessages } from '../../utils/validationMessages';
import VisitModalHeader from './VisitModalHeader';
import './ExecuteVisitModal.css';

/** Local calendar date as YYYY-MM-DD (for min/max on date inputs). */
const toLocalYmd = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  if (Number.isNaN(x.getTime())) return '';
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, '0');
  const day = String(x.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** Clip YYYY-MM-DD string to inclusive [minStr, maxStr] when both bounds exist. */
const clipYmd = (ymd, minStr, maxStr) => {
  let v = ymd || '';
  if (minStr && v && v < minStr) v = minStr;
  if (maxStr && v && v > maxStr) v = maxStr;
  return v;
};

const EXECUTION_NOTES_MAX_LENGTH = 2000;

const ExecuteVisitModal = ({ isOpen, onClose, visit, resolvedPiName, cras = [], onExecuted }) => {
  const [actualStartDate, setActualStartDate] = useState('');
  const [actualEndDate, setActualEndDate] = useState('');
  const [craId, setCraId] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  const [toast, setToast] = useState(null);

  useEffect(() => {
    if (isOpen && visit) {
      const todayYmd = toLocalYmd(new Date());
      const schedYmd = visit.plannedStartDate
        ? (typeof visit.plannedStartDate === 'string'
          ? visit.plannedStartDate
          : new Date(visit.plannedStartDate).toISOString()).split('T')[0]
        : '';
      const minYmd = schedYmd || '';
      const maxYmd = todayYmd;
      const defaultYmd = clipYmd(todayYmd, minYmd, maxYmd) || todayYmd;
      setActualStartDate(defaultYmd);
      setActualEndDate(defaultYmd);
      // CRA is bound to whoever was assigned at Schedule Visit; field is read-only
      setCraId(visit.cra_UserID != null ? String(visit.cra_UserID) : '');
      // Pre-fill any previously-saved Execute Visit notes (separate column from Schedule Notes).
      setNotes(visit.executionNotes != null ? String(visit.executionNotes) : '');
      setError(null);
    } else if (!isOpen) {
      setActualStartDate('');
      setActualEndDate('');
      setCraId('');
      setNotes('');
      setSaving(false);
      setError(null);
      setFieldErrors({});
      setToast(null);
    }
  }, [isOpen, visit]);

  if (!visit) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Execute Visit">
        <p>No visit selected.</p>
      </Modal>
    );
  }

  const doExecuteVisit = async (vRemarks) => {
    await visitService.execute(visit.scheduledVisitID, {
      actualStartDate: actualStartDate + 'T00:00:00Z',
      actualEndDate: actualEndDate + 'T00:00:00Z',
      cra_UserID: craId ? Number(craId) : (visit.cra_UserID != null ? visit.cra_UserID : null),
      vRemarks: vRemarks || undefined,
      notes: (notes || '').trim() || undefined,
    });
    if (onExecuted) onExecuted();
    setToast({ type: 'success', message: commonMessages.authSuccess });
    onClose();
  };

  const handleClear = () => {
    setError(null);
    setFieldErrors({});
    const todayYmd = toLocalYmd(new Date());
    const minYmd = scheduledStartStr || '';
    const maxYmd = todayYmd;
    const defaultYmd = clipYmd(todayYmd, minYmd, maxYmd) || todayYmd;
    setActualStartDate(defaultYmd);
    setActualEndDate(defaultYmd);
    setNotes('');
  };

  const validateForm = () => {
    const errs = {};
    const todayYmdVal = toLocalYmd(new Date());
    if (!(actualStartDate || '').trim()) errs.actualStartDate = visitMessages.actualStartRequired;
    else {
      if (actualStartDate > todayYmdVal) errs.actualStartDate = visitMessages.actualStartFuture;
    }
    if (!(actualEndDate || '').trim()) errs.actualEndDate = visitMessages.actualEndRequired;
    else {
      if (actualStartDate && actualEndDate < actualStartDate) errs.actualEndDate = visitMessages.actualEndBeforeStart;
      if (actualEndDate > todayYmdVal) errs.actualEndDate = errs.actualEndDate || visitMessages.actualEndFuture;
    }
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setFieldErrors({});
    if (!validateForm()) return;
    if (!visit.scheduledVisitID) return;
    void runAfterESign();
  };

  const runAfterESign = () => {
    const vRemarks = '';
    setSaving(true);
    doExecuteVisit(vRemarks).catch((err) => {
      console.error('Error executing visit:', err);
      const msg = err.response?.data?.error || err.message || 'Failed to execute visit. Please try again.';
      setError(msg);
      setToast({ type: 'error', message: msg });
    }).finally(() => setSaving(false));
  };

  const visitTypeLabel = VISIT_TYPES[visit.visitType] || visit.visitType || '—';
  const scheduledStartStr = visit.plannedStartDate
    ? (typeof visit.plannedStartDate === 'string' ? visit.plannedStartDate : new Date(visit.plannedStartDate).toISOString()).split('T')[0]
    : '';
  const scheduledEndStr = visit.plannedEndDate
    ? (typeof visit.plannedEndDate === 'string' ? visit.plannedEndDate : new Date(visit.plannedEndDate).toISOString()).split('T')[0]
    : '';

  const todayYmd = toLocalYmd(new Date());
  const actualDateMax = todayYmd;
  const actualDateMin = undefined;
  const actualEndMin = actualStartDate || undefined;

  // CRA options: ensure assigned CRA (from Schedule Visit) is in list so disabled dropdown displays correctly
  const craOptions = (() => {
    const hasAssignedInList = visit.cra_UserID != null && cras.some(c => c.id === String(visit.cra_UserID));
    if (visit.cra_UserID != null && visit.craName && !hasAssignedInList) {
      return [{ id: String(visit.cra_UserID), name: visit.craName || `CRA #${visit.cra_UserID}` }, ...cras];
    }
    return cras;
  })();

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="medium"
      title="Execute Visit"
      className="execute-visit-modal"
    >
      <form onSubmit={handleSubmit}>
        <VisitModalHeader
          projectCode={visit.projectCode}
          siteName={visit.siteName}
          siteCode={visit.siteCode ?? visit.SiteCode}
          piName={resolvedPiName ?? visit.piName ?? visit.PIName ?? visit.pIName}
        />

        <div className="form-group">
          <label className="form-label">Visit Type</label>
          <select className="form-select" disabled value={visit.visitType || ''}>
            <option value="">Select Visit Type</option>
            <option value={visit.visitType}>{visitTypeLabel}</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Scheduled Start Date</label>
          <input
            type="date"
            className="form-input"
            value={scheduledStartStr}
            disabled
            readOnly
          />
        </div>
        <div className="form-group">
          <label className="form-label">Scheduled End Date</label>
          <input
            type="date"
            className="form-input"
            value={scheduledEndStr}
            disabled
            readOnly
          />
        </div>

        <div className="form-group">
          <label className="form-label">Actual Start Date <span className="required">*</span></label>
          <input
            type="date"
            className="form-input"
            value={actualStartDate}
            min={actualDateMin}
            max={actualDateMax}
            onChange={(e) => {
              const v = e.target.value;
              setActualStartDate(v);
              setFieldErrors((prev) => ({ ...prev, actualStartDate: '' }));
              if (actualEndDate && v && actualEndDate < v) setActualEndDate(v);
            }}
          />
          {fieldErrors.actualStartDate && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.actualStartDate}</div>}
        </div>
        <div className="form-group">
          <label className="form-label">Actual End Date <span className="required">*</span></label>
          <input
            type="date"
            className="form-input"
            value={actualEndDate}
            min={actualEndMin}
            max={actualDateMax}
            onChange={(e) => { setActualEndDate(e.target.value); setFieldErrors(prev => ({ ...prev, actualEndDate: '' })); }}
          />
          {fieldErrors.actualEndDate && <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>{fieldErrors.actualEndDate}</div>}
        </div>

        <div className="form-group">
          <label className="form-label">CRA</label>
          <select
            className="form-select"
            value={craId}
            disabled
            title="CRA assigned at Schedule Visit (read-only)"
          >
            <option value="">Select CRA</option>
            {craOptions.map((cra) => (
              <option key={cra.id} value={cra.id}>
                {cra.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">Execution Notes</label>
          <textarea
            name="executionNotes"
            className="form-textarea"
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              setFieldErrors((prev) => ({ ...prev, notes: '' }));
            }}
            rows="4"
            maxLength={EXECUTION_NOTES_MAX_LENGTH}
            placeholder={`Enter Execution Notes (max ${EXECUTION_NOTES_MAX_LENGTH} characters)`}
          />
          {fieldErrors.notes && (
            <div className="field-error" style={{ color: '#c00', fontSize: '12px', marginTop: '4px' }}>
              {fieldErrors.notes}
            </div>
          )}
        </div>

        {error && (
          <div className="execute-visit-error" role="alert">
            {error}
          </div>
        )}

        <div className="execute-visit-footer">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={handleClear} disabled={saving}>
            Clear
          </button>
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </form>
    </Modal>
    {toast && (
      <div style={{ position: 'fixed', top: 16, right: 16, zIndex: 10000 }}>
        <Alert type={toast.type} message={toast.message} onClose={() => setToast(null)} />
      </div>
    )}
    </>
  );
};

export default ExecuteVisitModal;
